import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  Save, Users, Send, Eye, EyeOff, CheckCircle2,
  AlertCircle, Zap, TestTube2, Mail,
} from "lucide-react";

const PROVIDERS = ["SMTP", "Hostinger", "Gmail", "SendGrid", "SES"];

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
    {children}
    {hint ? <div className="text-[11px] text-gray-500 mt-1">{hint}</div> : null}
  </div>
);

export default function AffiliateEmailGateway() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/affiliate-email");
        setForm(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load affiliate email gateway");
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyHostingerPreset = () => {
    setForm((f) => ({
      ...f,
      provider: "Hostinger",
      smtp_host: "smtp.hostinger.com",
      smtp_port: 465,
      use_ssl: true,
      from_email: f.from_email || f.username || "",
      from_name: f.from_name || "Maxlive88 Affiliate",
    }));
    toast.message("Hostinger SMTP preset loaded (smtp.hostinger.com · 465 · SSL). Fill username = full affiliate email + Hostinger email password.");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/affiliate-email", form);
      setForm(data);
      toast.success("Affiliate email gateway saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const to = (testTo || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid recipient email to test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/affiliate-email/test", { to });
      setTestResult({ ok: true, id: data.id, to: data.to });
      toast.success(`Test affiliate email sent to ${data.to} ✅`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Test failed";
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500" data-testid="affiliate-email-loading">
        Loading...
      </div>
    );
  }

  const hasKey = !!(form.password || "").trim();

  return (
    <div data-testid="affiliate-email-gateway-page">
      <PageHeader
        title="Affiliate Email Gateway"
        subtitle="Dedicated SMTP for ALL affiliate / promo emails (separate from general, notification & agent gateways)"
      />

      {/* Scope banner */}
      <div className="max-w-3xl mb-5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-sm p-4 flex items-start gap-3" data-testid="affiliate-email-scope-banner">
        <div className="h-10 w-10 rounded-sm bg-emerald-500/15 text-emerald-700 inline-flex items-center justify-center shrink-0">
          <Users size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900">This gateway delivers ALL affiliate / promo emails</div>
          <ul className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-gray-700">
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Affiliate welcome &amp; credentials (on approval)</li>
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Password reset / re-issued credentials</li>
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Payout request approved (awaiting transfer)</li>
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Payout settled / paid (settlement receipt)</li>
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Payout rejected (with reason &amp; next steps)</li>
            <li className="flex items-center gap-1.5"><span className="text-emerald-600">✓</span> Commission credited (every 15 days)</li>
          </ul>
          <div className="text-[11px] text-gray-500 mt-2">Recommended dedicated mailbox: <code>affiliate@yourdomain.com</code>. Keeps marketing / payout traffic isolated from agent &amp; player notifications.</div>
        </div>
      </div>

      {/* Quick-start card */}
      <div className="max-w-3xl mb-5">
        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-sm p-4 flex items-start gap-3" data-testid="affiliate-hostinger-quickstart">
          <div className="h-10 w-10 rounded-sm bg-violet-500/15 text-violet-700 inline-flex items-center justify-center shrink-0">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Hostinger Business Email</div>
            <div className="text-[12px] text-gray-600 mt-1 leading-relaxed">
              SMTP via <code>smtp.hostinger.com:465 (SSL)</code>. Use a dedicated <code>affiliate@yourdomain.com</code> mailbox so affiliate / promo deliverability stays isolated from other traffic.
            </div>
            <button
              type="button"
              onClick={applyHostingerPreset}
              className="mt-2 h-8 px-3 text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white rounded-sm inline-flex items-center gap-1.5"
              data-testid="affiliate-apply-hostinger"
            >
              <Zap size={12} /> Apply Hostinger preset
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl bg-white border border-gray-200 rounded-sm p-6 space-y-5" data-testid="affiliate-email-form">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="h-10 w-10 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-sm flex items-center justify-center">
            <Users size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">Affiliate SMTP</div>
            <div className="text-xs text-gray-500">Used ONLY for affiliate / promo emails (welcome, password reset, payout lifecycle &amp; 15-day commission credited).</div>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
                data-testid="affiliate-email-enabled"
              />
              <span className="text-xs font-semibold uppercase tracking-wider">{form.enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Provider">
            <select
              value={form.provider || "Hostinger"}
              onChange={(e) => set("provider", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="affiliate-email-provider"
            >
              {PROVIDERS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>

          <Field label="SMTP Host">
            <input
              value={form.smtp_host || ""}
              onChange={(e) => set("smtp_host", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
              data-testid="affiliate-email-host"
              placeholder="smtp.hostinger.com"
            />
          </Field>

          <Field label="SMTP Port" hint="465 = SSL · 587 = STARTTLS">
            <input
              type="number"
              value={form.smtp_port || 465}
              onChange={(e) => {
                const port = parseInt(e.target.value) || 465;
                set("smtp_port", port);
                if (port === 465) set("use_ssl", true);
                if (port === 587 || port === 25 || port === 2525) set("use_ssl", false);
              }}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="affiliate-email-port"
            />
          </Field>

          <Field label="Username" hint="Full email address (e.g. affiliate@yourdomain.com)">
            <input
              value={form.username || ""}
              onChange={(e) => set("username", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="affiliate-email-username"
              placeholder="affiliate@maxlive88.com"
              autoComplete="off"
            />
          </Field>

          <Field label="Encryption">
            <div className="h-10 flex items-center gap-3 px-3 border border-gray-200 rounded-sm bg-gray-50">
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="affiliate-email-ssl"
                  checked={!!form.use_ssl}
                  onChange={() => set("use_ssl", true)}
                  data-testid="affiliate-email-ssl-on"
                />
                <span className="font-medium">SSL</span>
                <span className="text-[11px] text-gray-500">(465)</span>
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="affiliate-email-ssl"
                  checked={!form.use_ssl}
                  onChange={() => set("use_ssl", false)}
                  data-testid="affiliate-email-ssl-off"
                />
                <span className="font-medium">STARTTLS</span>
                <span className="text-[11px] text-gray-500">(587)</span>
              </label>
            </div>
          </Field>

          <div className="md:col-span-2">
            <Field label="Password">
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={form.password || ""}
                  onChange={(e) => set("password", e.target.value)}
                  className="w-full h-10 pl-3 pr-10 border border-gray-200 rounded-sm text-sm font-mono focus:outline-none focus:border-emerald-500"
                  data-testid="affiliate-email-password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Your Hostinger email account password"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showKey ? "Hide key" : "Show key"}
                  data-testid="affiliate-email-toggle-pw"
                >
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
          </div>

          <Field label="From Email">
            <input
              type="email"
              value={form.from_email || ""}
              onChange={(e) => set("from_email", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="affiliate-email-from"
              placeholder="affiliate@maxlive88.com"
            />
          </Field>
          <Field label="From Name">
            <input
              value={form.from_name || ""}
              onChange={(e) => set("from_name", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="affiliate-email-from-name"
              placeholder="Maxlive88 Affiliate"
            />
          </Field>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="affiliate-email-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Affiliate Gateway"}
          </button>
        </div>
      </form>

      {/* Test sender */}
      <div className="max-w-3xl mt-6 bg-white border border-gray-200 rounded-sm p-6" data-testid="affiliate-email-test-card">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="h-10 w-10 bg-sky-50 text-sky-600 border border-sky-200 rounded-sm flex items-center justify-center">
            <TestTube2 size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Send a test affiliate email</div>
            <div className="text-xs text-gray-500">Verifies SMTP credentials + From address + enabled flag end-to-end.</div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-3 mt-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Recipient email</label>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="your-inbox@example.com"
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-sky-500"
              data-testid="affiliate-email-test-to"
              disabled={!form.enabled || !hasKey}
            />
          </div>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing || !form.enabled || !hasKey}
            title={!form.enabled ? "Enable + save the gateway first" : !hasKey ? "Set the SMTP password first" : ""}
            className="h-10 px-5 bg-sky-500 hover:bg-sky-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="affiliate-email-test-send"
          >
            <Send size={14} /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
        {testResult && (
          <div
            className={`mt-4 flex items-start gap-2 p-3 rounded-sm text-xs border ${
              testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="affiliate-email-test-result"
          >
            {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5" /> : <AlertCircle size={14} className="mt-0.5" />}
            <div>
              {testResult.ok ? (
                <>
                  <div className="font-semibold">Delivered ✓</div>
                  <div className="opacity-80">Sent to <b>{testResult.to}</b>{testResult.id ? <> · id: <span className="font-mono">{testResult.id}</span></> : null}</div>
                </>
              ) : (
                <>
                  <div className="font-semibold">Failed</div>
                  <div className="font-mono opacity-80 break-all">{testResult.error}</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
